import { useEffect, useReducer, useRef } from 'react';
import MilestoneService from '../../api/milestoneService';
import { Milestone } from '../../types/milestone';
import toast from 'react-hot-toast';

type Action =
  | { type: 'fetch' }
  | { type: 'loaded'; value: Milestone[] | null }
  | { type: 'update'; milestone: Milestone }
  | { type: 'delete'; id: number }
  | { type: 'move'; fromLevel: number; toLevel: number };

interface MilestonesState {
  Records: Milestone[];
  Loading: boolean;
}

const DefaultMilestonesState = {
  Records: [],
  Loading: false,
} as MilestonesState;

const reducer = (state: MilestonesState, action: Action): MilestonesState => {
  switch (action.type) {
    case 'fetch':
      return { ...state, Loading: true };
    case 'loaded': {
      if (action.value === null) {
        return { ...state, Loading: false };
      }
      return { ...state, Records: [...action.value], Loading: false };
    }
    case 'update': {
      const index = state.Records.findIndex((r) => r.id === action.milestone.id);
      if (index === -1) {
        return { ...state, Records: [...state.Records, action.milestone] };
      }
      return {
        ...state,
        Records: state.Records.map((r, i) =>
          i === index ? action.milestone : r
        ),
      };
    }
    case 'delete': {
      return {
        ...state,
        Records: state.Records.filter((r) => r.id !== action.id),
      };
    }
    case 'move': {
      const fromMilestone = state.Records.find(
        (m) => m.levelNumber === action.fromLevel
      );
      const toMilestone = state.Records.find(
        (m) => m.levelNumber === action.toLevel
      );

      if (!fromMilestone) return state;

      if (toMilestone && toMilestone.type === fromMilestone.type) {
        return state;
      }

      const updatedRecords = state.Records.map((m) => {
        if (m.id === fromMilestone.id) {
          return { ...m, levelNumber: action.toLevel };
        }
        if (toMilestone && m.id === toMilestone.id) {
          return { ...m, levelNumber: action.fromLevel };
        }
        return m;
      });

      return { ...state, Records: updatedRecords };
    }
    default:
      return state;
  }
};

export const useMilestonesReducer = () => {
  const [data, dispatch] = useReducer(reducer, DefaultMilestonesState);
  const loading = useRef<boolean>(false);

  useEffect(() => {
    if (loading.current) return;
    loading.current = true;
    fetchData();
  }, []);

  const fetchData = async () => {
    dispatch({ type: 'fetch' });
    const records = await MilestoneService.getAll();
    dispatch({ type: 'loaded', value: records });
  };

  const create = async (milestone: Milestone): Promise<boolean> => {
    try {
      dispatch({ type: 'fetch' });
      const created = await MilestoneService.create(milestone);
      dispatch({ type: 'update', milestone: created });
      await fetchData();
      toast.success('Milestone created');
      return true;
    } catch (error) {
      toast.error('Failed to create milestone');
      return false;
    }
  };

  const update = async (milestone: Milestone): Promise<boolean> => {
    try {
      dispatch({ type: 'fetch' });
      const updated = await MilestoneService.update(milestone);
      dispatch({ type: 'update', milestone: updated });
      await fetchData();
      toast.success('Milestone updated');
      return true;
    } catch (error) {
      toast.error('Failed to update milestone');
      return false;
    }
  };

  const deleteMilestone = async (id: number): Promise<boolean> => {
    try {
      const result = await MilestoneService.delete(id);
      if (result === true) {
        dispatch({ type: 'delete', id });
        await fetchData();
        toast.success('Milestone deleted');
        return true;
      } else {
        toast.error('Failed to delete milestone');
        return false;
      }
    } catch (error) {
      toast.error('Failed to delete milestone');
      return false;
    }
  };

  const move = async (fromLevel: number, toLevel: number): Promise<boolean> => {
    const fromMilestone = data.Records.find((m) => m.levelNumber === fromLevel);
    const toMilestone = data.Records.find((m) => m.levelNumber === toLevel);

    if (!fromMilestone) return false;

    if (toMilestone && toMilestone.type === fromMilestone.type) {
      toast.error('Cannot move milestone to position with same type');
      return false;
    }

    dispatch({ type: 'move', fromLevel, toLevel });

    try {
      const updatedFrom = { ...fromMilestone, levelNumber: toLevel };
      await MilestoneService.update(updatedFrom);

      if (toMilestone) {
        const updatedTo = { ...toMilestone, levelNumber: fromLevel };
        await MilestoneService.update(updatedTo);
      }

      await fetchData();
      toast.success('Milestone moved');
      return true;
    } catch (error) {
      await fetchData();
      toast.error('Failed to move milestone');
      return false;
    }
  };

  return {
    data,
    create,
    update,
    deleteMilestone,
    move,
    fetchData,
  };
};
